import { format } from 'date-fns';

import type { Lesson, LessonGroup } from "../api/course-service";
import type { LessonStatus, ProgressLesson, UserProgress } from "../api/user-progress-service";

/**
 * What the timeline says about one lesson: the status the backend recorded, or that the lesson
 * carries too few questions to be verified at all.
 */
export type LessonMark = LessonStatus | 'DISABLED';

/** What the timeline says about one lesson group, summarising the lessons it can offer. */
export type GroupMark = 'DISABLED' | 'NOT_STARTED' | 'STARTED' | 'ALL_STARTED' | 'FINISHED';

export const LESSON_MARK_LABEL: Record<LessonMark, string> = {
    DISABLED: 'not enough questions',
    NOT_STARTED: 'not started',
    STARTED: 'recap due',
    FINISHED: 'finished',
};

export const GROUP_MARK_LABEL: Record<GroupMark, string> = {
    DISABLED: 'not enough questions',
    NOT_STARTED: 'not started',
    STARTED: 'in progress',
    ALL_STARTED: 'all started',
    FINISHED: 'all finished',
};

const NOT_STARTED: ProgressLesson = { status: 'NOT_STARTED', recapDate: null, sessions: [] };

/** A lesson the user never verified is absent from the progress, which reads as not started. */
export function lessonProgress(progress: UserProgress | null, lesson?: Lesson): ProgressLesson {
    if (!progress || !lesson) return NOT_STARTED;

    return progress.lessons[lesson.id] ?? NOT_STARTED;
}

export function lessonMark(lesson: Lesson, progress: UserProgress | null): LessonMark {
    if (!lesson.practicable) return 'DISABLED';

    return lessonProgress(progress, lesson).status;
}

/** Lessons that cannot be practiced are left out: they would block a group nobody can finish. */
export function groupMark(group: LessonGroup, progress: UserProgress | null): GroupMark {
    const statuses = group.lessons
        .filter((lesson) => lesson.practicable)
        .map((lesson) => lessonProgress(progress, lesson).status);

    if (!statuses.length) return 'DISABLED';
    if (statuses.every((status) => status === 'FINISHED')) return 'FINISHED';
    if (statuses.every((status) => status !== 'NOT_STARTED')) return 'ALL_STARTED';
    if (statuses.some((status) => status !== 'NOT_STARTED')) return 'STARTED';

    return 'NOT_STARTED';
}

/** The recap waits a week after the first session; both dates are plain days, so they compare as text. */
export function isRecapDue(recapDate: string | null): boolean {
    if (!recapDate) return false;

    return recapDate <= format(new Date(), 'yyyy-MM-dd');
}

/** How many Verification Sessions of the two a status stands for; drives the progress stepper. */
export function passedSessions(status: LessonStatus): number {
    if (status === 'FINISHED') return 2;
    if (status === 'STARTED') return 1;

    return 0;
}
